/**
 * Oppsummeringens tre lesninger. Ingen skrivinger — siden viser, den redigerer ikke.
 *
 * INGEN PAGINERING, av samme grunn som kontrakten oppgir: en årgang har titalls reiser og en
 * håndfull hoteller. Det er ikke en liste som vokser med organisasjonen, den vokser med ett
 * arrangement, og en markør over en dag-gruppert visning kunne delt en dag i to sider.
 *
 * `CACHE.list` og ikke `reference`: dette ER innholdet på siden, og det endrer seg hver gang noen
 * flytter et fly.
 */
import {
  performanceStayDtoSchema,
  performanceTravelDtoSchema,
  summaryFoodResponseSchema,
  summaryLogisticsResponseSchema,
  summaryStayResponseSchema,
  type CreatePerformanceStayRequest,
  type CreatePerformanceTravelRequest,
  type CreateSummaryMealRequest,
  type UpdateAdvanceDinnerRequest,
  type UpdatePerformanceStayRequest,
  type UpdatePerformanceTravelRequest,
  type UpdateSummaryMealRequest,
  type PerformanceStayDto,
  type PerformanceTravelDto,
  type SummaryFoodResponse,
  type SummaryLogisticsResponse,
  type SummaryStayResponse,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

export function summaryLogisticsQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.summaryTab(orgId, 'logistics', editionId),
    queryFn: ({ signal }): Promise<SummaryLogisticsResponse> =>
      apiRequest({
        path: `/summary/logistics?editionId=${editionId}`,
        schema: summaryLogisticsResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function summaryStayQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.summaryTab(orgId, 'stay', editionId),
    queryFn: ({ signal }): Promise<SummaryStayResponse> =>
      apiRequest({
        path: `/summary/stay?editionId=${editionId}`,
        schema: summaryStayResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function summaryFoodQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.summaryTab(orgId, 'food', editionId),
    queryFn: ({ signal }): Promise<SummaryFoodResponse> =>
      apiRequest({
        path: `/summary/food?editionId=${editionId}`,
        schema: summaryFoodResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

// ------------------------------------------------------------------------------------------- //
// Skrivinger: linjer som ikke hører til en act (sak 78.1 og 80.2)
// ------------------------------------------------------------------------------------------- //

/**
 * TO AV DE TRE SKRIVER TIL PROGRAMPOSTENS EGNE ENDEPUNKTER, og det er med vilje.
 *
 * En reise uten act er den samme raden i den samme tabellen som en reise på en booking — den
 * navngir bare årgangen i stedet for acten (`oneOwner` i kontrakten). Å gi Oppsummering et eget
 * `POST /summary/travel` ville vært en andre vei inn til den samme tabellen, med sitt eget
 * regelverk å holde i takt med det første.
 *
 * Måltidet går motsatt vei og har sitt eget endepunkt, fordi advance-endepunktet svarer med hele
 * advance-dokumentet til en act — noe et crewmåltid ikke har. Begrunnelsen står i kontrakten.
 *
 * ALLE TRE INVALIDERER HELE `qk.summary(orgId)`. En ny reise endrer dagens sum, og summen er
 * regnet på serveren; en klient som bare la raden inn i sin egen liste ville vist en overskrift
 * som ikke stemte med radene under den.
 */
export function useCreateSummaryTravel(
  orgId: string,
): UseMutationResult<PerformanceTravelDto, unknown, CreatePerformanceTravelRequest> {
  return useMutation({
    mutationFn: (body: CreatePerformanceTravelRequest) =>
      apiRequest({
        path: '/performance-travel',
        method: 'POST',
        body,
        schema: performanceTravelDtoSchema,
      }),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

export function useCreateSummaryStay(
  orgId: string,
): UseMutationResult<PerformanceStayDto, unknown, CreatePerformanceStayRequest> {
  return useMutation({
    mutationFn: (body: CreatePerformanceStayRequest) =>
      apiRequest({
        path: '/performance-stays',
        method: 'POST',
        body,
        schema: performanceStayDtoSchema,
      }),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

export function useCreateSummaryMeal(
  orgId: string,
): UseMutationResult<SummaryFoodResponse, unknown, CreateSummaryMealRequest> {
  return useMutation({
    mutationFn: (body: CreateSummaryMealRequest) =>
      apiRequest({
        path: '/summary/meals',
        method: 'POST',
        body,
        schema: summaryFoodResponseSchema,
      }),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

// ------------------------------------------------------------------------------------------- //
// Rette og fjerne en linje uten act (QA5 C-M4)
// ------------------------------------------------------------------------------------------- //

/*
 * RADENE HADDE INGEN KNAPPER. En linje lagt inn her er ikke knyttet til noen act, så den finnes
 * ikke på noen programpost heller — Oppsummering er det eneste stedet den kan rettes. Samme
 * endepunkter som oppretter dem, av grunnen headeren over gir.
 */
export function useUpdateSummaryTravel(
  orgId: string,
): UseMutationResult<
  PerformanceTravelDto,
  unknown,
  { id: string; body: UpdatePerformanceTravelRequest }
> {
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiRequest({
        path: `/performance-travel/${id}`,
        method: 'PATCH',
        body,
        schema: performanceTravelDtoSchema,
      }),
    /* En act-rad rettes også her (QA 13, 78), så Reise-fanen og mannskapet må lese på nytt. */
    meta: {
      invalidates: [qk.summary(orgId), qk.performanceTravel(orgId), qk.performancePeople(orgId)],
    },
  });
}

export function useRemoveSummaryTravel(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest({ path: `/performance-travel/${id}`, method: 'DELETE' }).then(() => undefined),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

export function useUpdateSummaryStay(
  orgId: string,
): UseMutationResult<
  PerformanceStayDto,
  unknown,
  { id: string; body: UpdatePerformanceStayRequest }
> {
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiRequest({
        path: `/performance-stays/${id}`,
        method: 'PATCH',
        body,
        schema: performanceStayDtoSchema,
      }),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

export function useRemoveSummaryStay(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest({ path: `/performance-stays/${id}`, method: 'DELETE' }).then(() => undefined),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

export function useUpdateSummaryMeal(
  orgId: string,
): UseMutationResult<SummaryFoodResponse, unknown, { id: string; body: UpdateSummaryMealRequest }> {
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiRequest({
        path: `/summary/meals/${id}`,
        method: 'PATCH',
        body,
        schema: summaryFoodResponseSchema,
      }),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}

/**
 * EN ACT SIN MIDDAG, RETTET FRA OPPSUMMERING (QA13 78). Den samme PATCH-en som skuffen på
 * programposten bruker (`/advance-dinners/:id`), så API-et spør programpostens tilgang. Svaret er
 * advancen; Oppsummering henter sin egen lesning på nytt.
 */
export function useUpdateActDinner(
  orgId: string,
): UseMutationResult<unknown, unknown, { id: string; body: UpdateAdvanceDinnerRequest }> {
  return useMutation({
    mutationFn: ({ id, body }) =>
      apiRequest({ path: `/advance-dinners/${id}`, method: 'PATCH', body }),
    meta: { invalidates: [qk.summary(orgId), qk.performanceAdvance(orgId)] },
  });
}

export function useRemoveSummaryMeal(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest({ path: `/summary/meals/${id}`, method: 'DELETE' }).then(() => undefined),
    meta: { invalidates: [qk.summary(orgId)] },
  });
}
